// Page component rendering the provided settings

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSettingsRepo } from "../db/useSettingsRepo";
import SettingsCategory from "../components/SettingsCategory";
import { useTheme } from "../../../providers/ThemeProvider";

// import "./settings.css";

export default function SettingsPage() {
  const [allSettings, setAllSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const repo = useSettingsRepo();
  const { setThemeMode, setThemeAccent } = useTheme();

  const onChangeMode = async (modeObj) => {
    setThemeMode(modeObj.value); // instant UI
  };

  const onChangeAccent = async (colorOpt) => {
    setThemeAccent(colorOpt.value);
  };

  function useDebouncedCallback(fn, delayMs, deps = []) {
    const timeoutRef = useRef(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useCallback(
      (...args) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => fn(...args), delayMs);
      },
      [fn, delayMs, ...deps]
    );
  }

  const debouncedPersist = useDebouncedCallback(
    async (id, value) => {
      try {
        await repo.put(value); // persist full object with new value
      } catch (e) {
        console.error("Failed saving setting", value.id, e);
        // [ ] optional: revert setting change on failure
        setAllSettings((prev) => prev); // no-op; insert real revert logic if needed
      }
    },
    250,
    []
  );

  const handleUpdate = useCallback(
    async (id, newVal) => {
      let updatedSetting = null;

      // Update the state and get the updated object in one pass
      await setAllSettings((prev) => {
        return prev.map((s) => {
          if (s.id === id) {
            // Create the new object
            const newSetting = { ...s, value: newVal, modified: Date.now() };
            // Capture the updated object in the variable
            updatedSetting = newSetting;
            // Return the new object for the map function
            return newSetting;
          }
          return s;
        });
      });

      // setAllSettings((prev) =>
      //   prev.map((s) =>
      //     s.id === id ? { ...s, value: newVal, modified: Date.now() } : s
      //   )
      // );
      if (updatedSetting.id === "theme-mode") onChangeMode(updatedSetting);
      if (updatedSetting.id === "theme-accent") onChangeAccent(updatedSetting);
      debouncedPersist(id, updatedSetting);
    },
    [debouncedPersist]
  );

  // get all settings
  useEffect(() => {
    (async () => {
      try {
        const data = await repo.getAll();
        // if (mounted) {
        setAllSettings(data);
        setLoading(false);
        // }
      } catch (e) {
        setError(e?.message || "Failed to load settings");
        console.error("Failed to fetch settings:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []); // run once

  // ======================================================
  //  Build ordered categories + grouped settings
  // ======================================================
  function buildSettingsModel(allSettings) {
    // Find the parent settings object
    const parent = allSettings.find((s) => s.id === "settings");
    if (!parent) {
      return { categoriesOrdered: [], grouped: new Map(), parent: null };
    }

    // Build categories ordered by `order`
    const categoriesOrdered = [...(parent.categories || [])]
      .filter((c) => c && typeof c.id === "number")
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    // Group settings by category id (skip parent itself)
    const grouped = new Map();
    for (const cat of categoriesOrdered) grouped.set(cat.id, []);

    for (const s of allSettings) {
      if (!s || s.id === "settings") continue;
      if (s.deleted) continue; // skip tombstoned if using soft deletes

      const cid = typeof s.category === "number" ? s.category : null;
      if (cid != null && grouped.has(cid)) {
        grouped.get(cid).push(s);
      }
    }

    // Optional: stable sort within each category by `order` then `label`
    for (const [, list] of grouped) {
      list.sort((a, b) => {
        const ao = a.order ?? 0;
        const bo = b.order ?? 0;
        if (ao !== bo) return ao - bo;
        return String(a.label || a.id).localeCompare(String(b.label || b.id));
      });
    }

    return { categoriesOrdered, grouped, parent };
  }

  // Build the model once per data change
  const { categoriesOrdered, grouped } = useMemo(
    () => buildSettingsModel(allSettings),
    [allSettings]
  );

  if (loading) return <div>Loading settings...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="settings-container">
      {categoriesOrdered.length === 0 ? (
        <div>No categories defined.</div>
      ) : (
        categoriesOrdered.map((cat) => (
          <SettingsCategory
            key={cat.id}
            category={cat}
            settings={grouped.get(cat.id) || []}
            onUpdate={handleUpdate}
          />
        ))
      )}
    </div>
  );
}
